import {
  Component,
  contentChild,
  viewChild,
  TemplateRef,
  ChangeDetectionStrategy,
  inject,
  input,
  signal,
  computed
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { MatLegacyButton as MatButton } from '@angular/material/legacy-button';
import { Store, select } from '@ngrx/store';
import { selectZefProgressesByType, ButtonProgressModule } from '@zerops/zef/progress';
import {
  selectZefErrorsByType,
  zefRemoveError,
  ZefErrorTemplateService
} from '@zerops/zef/errors';
import { SatPopoverAnchor, SatPopoverHorizontalAlign, SatPopoverVerticalAlign, SatPopoverModule } from '@zerops/zef/popover';
import { ZefReactiveComponent, ZefKeyInput } from '@zerops/zef/core';
import { timer, Subject } from 'rxjs';
import {
  switchMap,
  map,
  tap,
  delayWhen,
  withLatestFrom,
  filter,
  pairwise,
  mergeMap
} from 'rxjs/operators';

@Component({
  selector: 'zef-progress-error',
  templateUrl: './progress-error.container.html',
  styleUrls: [ './progress-error.container.scss' ],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    SatPopoverModule,
    ButtonProgressModule
  ],
  host: {
    '[class.is-full]': 'full()'
  }
})
export class ProgressErrorContainer extends ZefReactiveComponent {

  // # Deps
  #store = inject(Store);
  #errorTemplates = inject(ZefErrorTemplateService);

  // # Event Streams
  onPopoverClosed$ = new Subject<void>();

  // # Data
  // -- sync
  tpl = signal<TemplateRef<any> | undefined>(undefined);

  // -- angular
  key = input<ZefKeyInput>();
  full = input(false, {
    transform: (v: string | boolean) => coerceBooleanProperty(v)
  });
  loading = input(false);
  disabled = input(false);
  disabledMode = input<'apply-both' | 'progress-only'>('apply-both');
  popVerticalAlign = input<SatPopoverVerticalAlign>('above');
  popHorizontalAlign = input<SatPopoverHorizontalAlign>('center');

  buttonRef = contentChild(MatButton);
  popoverAnchorRef = viewChild(SatPopoverAnchor);

  // -- async
  key$ = toObservable(this.key);

  progressActive$ = this.key$.pipe(
    filter((keyOrKeys) => !!keyOrKeys),
    switchMap((keyOrKeys) => this.#store.pipe(
      select(selectZefProgressesByType(keyOrKeys)),
    )),
    map((d) => !!d.length),
    delayWhen((v) => !v ? timer(200) : timer(0))
  );

  errorData$ = this.key$.pipe(
    filter((keyOrKeys) => !!keyOrKeys),
    switchMap((keyOrKeys) => this.#store.pipe(
      select(selectZefErrorsByType(keyOrKeys)),
    ))
  );

  errorsActive$ = this.errorData$.pipe(
    map((d) => d.filter((i) => i.errorType === 'pop')),
    tap((errs) => {
      const popoverRef = this.popoverAnchorRef();
      if (popoverRef) {
        // checked after change error
        setTimeout(() => {
          this.tpl.set(this.#errorTemplates.getTemplate('pop'));
          if (errs?.length) {
            popoverRef.popover.open();
          } else {
            popoverRef.popover.close();
          }
        });
      }
    }),
    pairwise(),
    map(([ a, b ]) => a.length ? a : b),
    delayWhen((d) => !d?.length ? timer(100) : timer(0))
  );

  // -- signals
  progressActiveSignal = toSignal(this.progressActive$, { initialValue: false });

  // -- computed
  effectiveDisabled = computed(() => {
    const mode = this.disabledMode();
    const disabled = this.disabled();
    const progressActive = this.progressActiveSignal();

    if (mode === 'progress-only' && progressActive) {
      return false;
    }
    return disabled;
  });

  // # State resolver
  state = this.$connect({
    errorData: this.errorData$,
    errorsActive: this.errorsActive$
  });

  // # Dispatcher
  constructor() {
    super();

    this.$dispatchActions([
      this.onPopoverClosed$.pipe(
        withLatestFrom(this.errorData$),
        filter(([ _, errorData ]) => !!errorData.length),
        // Remove ALL errors that were displayed in the popover
        mergeMap(([ _, errorData ]) =>
          errorData.map((error) => zefRemoveError(error.key, error.id ? String(error.id) : undefined))
        )
      )
    ]);
  }
}
